import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';

import {
  SpsImportListe,
  SpsListenUpdateEintrag,
  SpsListenUpdateErgebnis,
  SpsListenUpdateKonflikt,
  SpsListenUpdateVorschau,
  listenTypInfo,
  pruefstatusText
} from '../../../../../Models/Sps';
import { SpsImportApiService } from '../../../../../Services/sps-import-api.service';
import { SpsPruefungApiService } from '../../../../../Services/sps-pruefung-api.service';

export interface SpsListenUpdateDialogData {
  liste: Pick<SpsImportListe, 'id' | 'name' | 'listenTyp' | 'dateiName' | 'eintraegeAnzahl' | 'anlagenteilSpalte'>;
}

/**
 * „Liste aktualisieren“: eine neuere Excel-Version derselben SPS-Liste hochladen.
 *
 *  1. Datei wählen → Vorschau vom Server (unverändert / aktualisiert / neu / gelöscht /
 *     Konflikte / zuvor manuell gelöscht, betroffene geprüfte Signale). Speichert nichts.
 *  2. Bestätigen → das Update läuft im Backend in einer Transaktion; die Datei wird
 *     dafür ein zweites Mal hochgeladen (der Server hält keinen Zustand).
 *
 * Liefert das Backend-Ergebnis oder null (abgebrochen).
 */
@Component({
  selector: 'app-sps-listen-update-dialog',
  templateUrl: './sps-listen-update-dialog.component.html',
  styleUrls: ['./sps-listen-update-dialog.component.css']
})
export class SpsListenUpdateDialogComponent {

  datei: File | null = null;
  sheetName: string | null = null;
  vorschau: SpsListenUpdateVorschau | null = null;
  laedt = false;
  wendetAn = false;
  fehler = '';
  geloeschteWiederherstellen = false;

  /** Nummer der letzten Vorschau-Anfrage – ältere Antworten werden verworfen. */
  private anfrage = 0;

  constructor(
    private dialogRef: MatDialogRef<SpsListenUpdateDialogComponent, SpsListenUpdateErgebnis | null>,
    @Inject(MAT_DIALOG_DATA) public data: SpsListenUpdateDialogData,
    private api: SpsImportApiService
  ) { }

  get typTitel(): string {
    return listenTypInfo(this.data.liste.listenTyp).titel;
  }

  get typIcon(): string {
    return listenTypInfo(this.data.liste.listenTyp).icon;
  }

  dateiGewaehlt(event: Event): void {
    const input = event.target as HTMLInputElement;
    const datei = input.files?.[0] ?? null;
    input.value = '';

    if (!datei) {
      return;
    }

    this.datei = datei;
    this.sheetName = null;
    this.vorschauLaden();
  }

  blattWechseln(event: Event): void {
    const blatt = (event.target as HTMLSelectElement).value;

    if (!blatt || blatt === this.sheetName) {
      return;
    }

    this.sheetName = blatt;
    this.vorschauLaden();
  }

  vorschauLaden(): void {
    if (!this.datei) {
      return;
    }

    const nummer = ++this.anfrage;
    this.laedt = true;
    this.fehler = '';
    this.vorschau = null;

    this.api.updateVorschau(this.data.liste.id, this.datei, this.sheetName)
      .pipe(finalize(() => {
        if (nummer === this.anfrage) {
          this.laedt = false;
        }
      }))
      .subscribe({
        next: v => {
          if (nummer !== this.anfrage) {
            return;
          }

          this.vorschau = v;
          this.sheetName = v.blatt;
        },
        error: err => {
          if (nummer === this.anfrage) {
            this.fehler = SpsPruefungApiService.fehlertext(err, 'Die Vorschau konnte nicht geladen werden.');
          }
        }
      });
  }

  get kannAnwenden(): boolean {
    const v = this.vorschau;
    return !!this.datei && !!v && v.typPasst && !this.laedt && !this.wendetAn
      && (!v.ohneUnterschied || (this.geloeschteWiederherstellen && v.zuvorManuellGeloescht > 0));
  }

  get blockiertGrund(): string | null {
    if (!this.datei) {
      return 'Bitte zuerst die neue Excel-Datei wählen.';
    }

    if (this.laedt) {
      return 'Die Vorschau wird noch geladen.';
    }

    if (!this.vorschau) {
      return this.fehler || 'Die Vorschau ist noch nicht geladen.';
    }

    if (!this.vorschau.typPasst) {
      return 'Die Datei passt nicht zum Listentyp – Pflichtspalten fehlen.';
    }

    if (this.vorschau.ohneUnterschied && !(this.geloeschteWiederherstellen && this.vorschau.zuvorManuellGeloescht > 0)) {
      return 'Die neue Datei ist inhaltlich identisch mit dem Bestand – es gibt nichts zu aktualisieren.';
    }

    return null;
  }

  anwenden(): void {
    if (!this.kannAnwenden || !this.datei) {
      return;
    }

    this.wendetAn = true;
    this.fehler = '';

    this.api.updateAnwenden(this.data.liste.id, this.datei, {
      sheetName: this.sheetName,
      geloeschteWiederherstellen: this.geloeschteWiederherstellen
    })
      .pipe(finalize(() => this.wendetAn = false))
      .subscribe({
        next: ergebnis => this.dialogRef.close(ergebnis),
        error: err => this.fehler = SpsPruefungApiService.fehlertext(err, 'Das Listenupdate ist fehlgeschlagen. Es wurde nichts geändert.')
      });
  }

  abbrechen(): void {
    this.dialogRef.close(null);
  }


  // ── Anzeige ──

  statusText(e: SpsListenUpdateEintrag): string {
    return e.pruefstatus ? pruefstatusText(e.pruefstatus) : '–';
  }

  aenderungText(e: SpsListenUpdateEintrag): string {
    const teile = e.aenderungen.map(a => `${a.feld}: „${a.alt ?? ''}“ → „${a.neu ?? ''}“`);

    if (e.anlagenteilNeu !== null || e.anlagenteilAlt !== null) {
      teile.push(`Anlagenteil: „${e.anlagenteilAlt ?? ''}“ → „${e.anlagenteilNeu ?? ''}“`);
    }

    return teile.join(' · ');
  }

  zeileText(e: SpsListenUpdateEintrag): string {
    const zeile = e.neueZeile ?? e.alteZeile;
    return zeile != null ? `Zeile ${zeile}` : '';
  }

  trackByEintrag(index: number, e: SpsListenUpdateEintrag): string {
    return `${e.eintragId ?? 'neu'}-${e.neueZeile ?? ''}-${index}`;
  }

  trackByKonflikt(index: number, k: SpsListenUpdateKonflikt): string {
    return `${k.schluessel}-${index}`;
  }

  /** Alle Detaillisten sind auf 300 Einträge begrenzt; die Zähler gelten für alles. */
  gekuerzt(liste: SpsListenUpdateEintrag[], gesamt: number): boolean {
    return gesamt > liste.length;
  }
}
